import {Fragment} from 'react'
import {NavList, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon} from '@primer/octicons-react'

import type {PreviewStackPull} from '../../../page-data/payloads/preview-stack'
import {ExistingStackDivider} from './ExistingStackDivider'
import {PreviewStackPullItem} from './PreviewStackPullItem'
import styles from './PreviewStackDialog.module.css'

export type PreviewStackListProps = {
  pulls: PreviewStackPull[]
  isPending: boolean
  showFailure: boolean
  onRemoveTop: () => void
  disableRemove: boolean
  currentPullNumber: number
  containerRef: React.RefObject<HTMLDivElement | null>
}

export function PreviewStackList({
  pulls,
  isPending,
  showFailure,
  onRemoveTop,
  disableRemove,
  currentPullNumber,
  containerRef,
}: PreviewStackListProps) {
  const hasPulls = !isPending && pulls.length > 0
  const lastIndex = pulls.length - 1
  // Pulls are top-first: newly-added pulls sit above the existing stack they
  // are being appended to, so any already-stacked pulls form a contiguous
  // suffix. `firstExistingIndex` is the boundary (also the count of
  // newly-added pulls); when it is -1 there is no existing stack
  const firstExistingIndex = pulls.findIndex(pull => pull.stackId != null)
  const hasExistingStack = firstExistingIndex !== -1
  // Pulls already in an existing stack collectively count as one stackable
  // unit, since removing the top standalone pull would still leave a valid
  // 2-pull stack (the existing one).
  const stackablePullCount = hasExistingStack ? firstExistingIndex + 1 : pulls.length
  const canRemoveTop = stackablePullCount > 2

  return (
    <div
      ref={containerRef}
      role="region"
      className={styles.stackContainer}
      aria-label="Proposed stack of pull requests"
      aria-busy={isPending}
    >
      {isPending ? (
        <Spinner srText="Loading proposed stack" />
      ) : showFailure ? (
        <Blankslate border={false} spacious={false}>
          <Blankslate.Visual>
            <AlertIcon size="medium" className="fgColor-muted" />
          </Blankslate.Visual>
          <Blankslate.Heading as="h2">Preview failed to generate</Blankslate.Heading>
          <Blankslate.Description>Try refreshing the page.</Blankslate.Description>
        </Blankslate>
      ) : hasPulls ? (
        <NavList className={styles.stackNavList} disableItemGap>
          {pulls.map((pull, index) => {
            const onRemove = canRemoveTop && index === 0 ? onRemoveTop : undefined
            const isCurrent = pull.number === currentPullNumber
            const dividerStackNumber = hasExistingStack && index === firstExistingIndex ? pull.stackNumber : null
            return (
              <Fragment key={pull.number}>
                {dividerStackNumber != null ? <ExistingStackDivider stackNumber={dividerStackNumber} /> : null}
                <PreviewStackPullItem
                  pull={pull}
                  isCurrent={isCurrent}
                  isTail={index === lastIndex}
                  onRemove={onRemove}
                  disableRemove={disableRemove}
                />
              </Fragment>
            )
          })}
        </NavList>
      ) : null}
    </div>
  )
}
